import { Vector3 } from 'three';
import HalfEdgeMesh from '@three/common/halfEdgeMesh/HalfEdgeMesh';
import { Vertex } from '@/thirdPartyLibraries/three-mesh-halfedge/src';

export type FalloffType = 'exponential' | 'linear' | 'gaussian';

export interface PushTowardsPositionOptions {
  /** How many BFS rings to affect (0 = only seeds). Default 2 */
  rings?: number;
  /** Exponential factor for ring decay (used when falloff='exponential'). Default 0.6 */
  falloff?: number;
  /** Falloff profile across rings. Default 'exponential' */
  falloffType?: FalloffType;
  /** Max absolute step per vertex to prevent overshoot. Default 0.6 * mean edge */
  maxStep?: number;
  /** Step fraction relative to distance-to-target for ring 0. Default 0.5 */
  fraction?: number;
  /** Smoothing iterations on affected region. Default 1 */
  iterations?: number;
}

/**
 * Pushes the selected vertex/vertices and their ring-neighborhood toward
 * a target position with ring-based falloff and safe step clamping.
 */
export class PushVerticesTowardsPosition {
  constructor(private mesh: HalfEdgeMesh) {}

  public async apply(
    seeds: Vertex[] | Vertex,
    target: Vector3,
    options: PushTowardsPositionOptions = {}
  ): Promise<void> {
    const ds = this.mesh.halfEdges;
    const seedArray = Array.isArray(seeds) ? seeds : [seeds];
    if (seedArray.length === 0) return;

    const meanEdge = Math.max(ds.getMeanEdgeLength?.() ?? 0.05, 1e-6);
    const rings = Math.max(0, Math.floor(options.rings ?? 2));
    const falloff = Math.min(0.99, Math.max(0.0, options.falloff ?? 0.6));
    const falloffType = options.falloffType ?? 'exponential';
    const maxStep = Math.max(1e-6, options.maxStep ?? 0.6 * meanEdge);
    const fraction = Math.min(0.99, Math.max(0.01, options.fraction ?? 0.5));
    const iterations = Math.max(0, Math.floor(options.iterations ?? 1));

    // Build ring map by combining per-seed rings, taking the minimum ring index per vertex
    const ringMap = new Map<Vertex, number>();
    for (const seed of seedArray) {
      if (!seed) continue;
      // ring 0 is the seed itself
      if (!ringMap.has(seed)) ringMap.set(seed, 0);
      const seedRings = seed.getRings(rings);
      for (let r = 0; r < seedRings.length; r++) {
        for (const v of seedRings[r]) {
          const existing = ringMap.get(v);
          const ringIdx = r + 1; // getRings[0] is first ring
          if (existing === undefined || ringIdx < existing) ringMap.set(v, ringIdx);
        }
      }
    }

    // Precompute Gaussian sigma if needed
    const sigma = Math.max(1e-3, rings > 0 ? rings * 0.75 : 1);
    const weightForRing = (r: number): number => {
      switch (falloffType) {
        case 'linear': {
          const t = rings > 0 ? 1 - r / rings : 1;
          return Math.max(0, t);
        }
        case 'gaussian':
          return Math.exp(-(r * r) / (2 * sigma * sigma));
        case 'exponential':
        default:
          return Math.pow(falloff, r);
      }
    };

    // Apply push with chosen falloff and clamping
    for (const [v, r] of ringMap.entries()) {
      const dir = new Vector3().subVectors(target, v.position);
      const dist = dir.length();
      if (!(dist > 1e-9)) continue;
      dir.multiplyScalar(1 / dist);

      let step = dist * fraction * weightForRing(r);
      // never overshoot target
      if (step > dist) step = dist;
      // clamp per-vertex step
      if (step > maxStep) step = maxStep;
      v.position.addScaledVector(dir, step);
    }

    // Optional gentle smoothing on affected region (keep seeds stiffer)
    if (iterations > 0) {
      const region = Array.from(ringMap.keys());
      for (let it = 0; it < iterations; it++) {
        const nextPos = new Map<Vertex, Vector3>();
        for (const u of region) {
          const rr = ringMap.get(u)!;
          const alpha = rr === 0 ? 0.15 : 0.25;
          if (alpha <= 0) continue;
          const nbrs = Array.from(u.adjacentVertices()).filter((n) => ringMap.has(n));
          if (nbrs.length === 0) continue;
          const avg = new Vector3();
          for (const n of nbrs) avg.add(n.position);
          avg.multiplyScalar(1 / nbrs.length);
          nextPos.set(u, u.position.clone().lerp(avg, alpha));
        }
        for (const [u, p] of nextPos) u.position.copy(p);
      }
    }
  }
}

